'use client';

import React from 'react';
import { cj } from 'src/utils/classNames';
import { Wallet, Details, PointsMono } from '@velocity-exchange/icons';
import {
	ActionButtonConfig,
	EarnAccordionOptions,
} from 'src/hooks/useEarnProduct';
import { EarnActionButton } from 'src/components/Earn/EarnActionButton';
import MarketIcon from '../../Utils/MarketIcon';
import ApyBreakdownTooltipV2 from '../../Tooltip/ApyBreakdownTooltipV2';
import { useEarnProductData } from 'src/hooks/useEarnProductData';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { getColorVar } from 'src/utils/theme/colorVars';

interface EarnProductDesktopProps {
	earnProduct: EarnAccordionOptions;
	showDetails: boolean;
	belongsToLastProductRow: boolean;
}

export const EarnProductDesktop: React.FC<EarnProductDesktopProps> = ({
	earnProduct,
	showDetails,
	belongsToLastProductRow,
}) => {
	const {
		cleanedMarketSymbol,
		finalProductData,
		apyData,
		shouldShowWalletBalance,
		getWalletBalance,
		apyStr,
	} = useEarnProductData(earnProduct);

	const isStrategyVault = earnProduct.productType === 'strategy_vaults';

	const filteredActionButtons = earnProduct.actionButtons.filter(
		(button: ActionButtonConfig) => button.type !== 'details'
	);

	return (
		<div
			className={cj(
				'col-span-3 grid grid-cols-subgrid border-b border-stroke-divider bg-container-bg py-lg pr-2xl hover:bg-button-disabled',
				belongsToLastProductRow && 'last:rounded-b-sm last:border-b-0'
			)}
		>
			{showDetails ? (
				<div className="flex items-center gap-sm pl-xl">
					<div className="inline-flex items-center">
						<MarketIcon
							sizeClass="min-w-[40px] min-h-[40px]"
							marketSymbol={cleanedMarketSymbol}
						/>
					</div>
					<div className="flex flex-col justify-center gap-sm pl-lg">
						<div className="flex items-center gap-sm">
							<span className="typo-label-md text-text-default">
								{cleanedMarketSymbol}
							</span>
						</div>

						<div className="flex items-center gap-sm">
							{shouldShowWalletBalance && (
								<div className="flex items-center gap-xs text-text-secondary">
									<Wallet />
									<span className="typo-body-xs">
										{getWalletBalance(cleanedMarketSymbol)}
									</span>
								</div>
							)}
							<div className="flex items-center gap-sm">
								<span className="typo-label-md whitespace-nowrap text-text-default">
									{finalProductData.title}
								</span>
							</div>
						</div>
					</div>
				</div>
			) : (
				<div className="flex items-center pl-20 typo-label-md text-text-default">
					{finalProductData.title}
				</div>
			)}

			<div className="flex items-center justify-end text-end">
				<div className="flex items-center gap-xs">
					{(finalProductData.promoApy ?? 0) > 0 && (
						<PointsMono size={16} color={getColorVar('purple-link')} />
					)}
					<div className="flex flex-col gap-xs">
						<span className="text-end text-text-secondary">
							{earnProduct.isRangeApy &&
							!(
								earnProduct.productType === 'strategy_vaults' &&
								earnProduct.apyToShow > 30
							)
								? 'Up to'
								: ''}
						</span>
						<ApyBreakdownTooltipV2
							className="text-text-secondary"
							apyChunks={apyData.deposit.chunks}
							showTooltip={apyData.deposit.showTooltip && !isStrategyVault}
						>
							<span className="typo-label-md text-text-positive-green-button">
								{apyStr}
							</span>
						</ApyBreakdownTooltipV2>
					</div>
				</div>
			</div>
			<div className="flex items-center justify-end gap-md">
				{finalProductData.apyInfoAction && (
					<IconButton
						size="lg"
						onClick={finalProductData.apyInfoAction}
						icon={Details}
						aria-label="View APY details"
					/>
				)}
				{filteredActionButtons.map(
					(actionButton: ActionButtonConfig, i: number) => (
						<EarnActionButton
							productType={earnProduct.productType}
							key={`action_button_${i}`}
							type={actionButton.type}
							marketIndex={earnProduct.marketIndex}
							marketSymbol={earnProduct.marketSymbol}
							highlight={actionButton.highlight}
							mobileText={actionButton.mobileText}
							className={cj(
								'items-center',
								filteredActionButtons.length > 1 && 'w-[92px]'
							)}
							size="MEDIUM"
						/>
					)
				)}
			</div>
		</div>
	);
};
